import React, { useState } from "react";
import "./TemplateButtons.scss";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CommonEnums } from "../../../../enums/common.enums";
import { getImageUrl } from "../../../../utils/helpers/common";
import { PropertyApi } from "../../../../utils/api/Property";
import { templatesAction } from "../../../../redux/features/templatesSlice";
import LockIcon2 from "../../../../components/icons/LockIcon2";
import RoundTrueIcon from "../../../../components/icons/RoundTrueIcon";
import PreviewModal from "../propertypost/view/PreviewModal";
import { AuthHelpers } from "../../../../utils/auth.helpers";

interface stateType {
  isLoading: boolean;
}

const checkVideo = (type: string) => {
  switch (type) {
    case CommonEnums?.postType?.reel_post?.key:
    case CommonEnums?.postType?.video_post?.key:
      return true;
    default:
      return false;
  }
};

const TemplateButtons = ({
  templateData,
  onTemplateSelect,
  announcementPostId: propAnnouncementPostId,
}: {
  templateData: any;
  onTemplateSelect: (selectedTemplateId: string) => void;
  announcementPostId?: string;
}) => {
  const [state, setState] = useState<stateType>({ isLoading: false });
  const dispatch = useDispatch();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [searchParams] = useSearchParams();

  const announcementPostId =
    propAnnouncementPostId || searchParams.get("announcement_post_id");

  const userRole = AuthHelpers.getUserType();

  const onClickSelectTemplate = async () => {
    if (!announcementPostId) {
      toast.error("Announcement post ID is missing from URL or props.");
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const designTemplateId =
        templateData?.design_template?._id || templateData?.design_template_id;

      if (!designTemplateId) {
        toast.error("Design template ID is missing.");
        return;
      }

      const payload = {
        design_template_id: designTemplateId,
        finalize: true,
      };

      const res = await PropertyApi.updateAnnouncementPost(
        payload,
        announcementPostId
      );

      if (res?.data?.success) {
        toast.success("Template selected successfully.");
        dispatch(templatesAction?.resetTemplateDesign());
        dispatch(templatesAction?.setSelectedTemplateId(templateData?._id));
        onTemplateSelect(templateData?._id);
      } else {
        toast.error(
          res?.data?.message || "Failed to update announcement post."
        );
      }
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong.");
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const isDisabled =
    state.isLoading ||
    templateData?.selected ||
    !["published", "active"].includes(templateData?.status);

  const getPreviewImage = (designTemplate: any) => {
    if (!designTemplate) return "";

    const isAgency = userRole === CommonEnums.userRole.agency;

    if (templateData?.post_type === CommonEnums.postTypes.image_post) {
      return isAgency
        ? designTemplate?.agency_image_preview_file_ref?.compressed_file_path
        : designTemplate?.agent_image_preview_file_ref?.compressed_file_path;
    }

    if (templateData?.post_type === CommonEnums.postTypes.story_post) {
      return isAgency
        ? designTemplate?.agency_story_preview_file_ref?.compressed_file_path
        : designTemplate?.agent_story_preview_file_ref?.compressed_file_path;
    }

    return "";
  };

  const handleOpen = (data: any) => {
    const imagePath = getPreviewImage(data?.design_template);

    if (imagePath) {
      setPreviewImages([getImageUrl(imagePath)]);
      setPreviewOpen(true);
    } else {
      toast.error("No preview available for this template.");
    }
  };

  return (
    <>
      <div className="template-box-btn">
        <button
          className="preview-btn box-btn"
          onClick={() => handleOpen(templateData)}
        >
          Preview
        </button>

        <button
          className={`select-btn box-btn ${
            templateData?.selected
              ? "button-active"
              : templateData?.status !== CommonEnums?.status?.published
              ? "button-disable"
              : ""
          }`}
          disabled={isDisabled}
          onClick={onClickSelectTemplate}
        >
          {state.isLoading ? (
            <div className="css-loading-spinner"></div>
          ) : (
            <>
              <span className="lock-icon">
                <LockIcon2 />
              </span>
              <span className="round-true-icon">
                <RoundTrueIcon />
              </span>
              {templateData?.selected ? "Selected" : "Select"}
            </>
          )}
        </button>
      </div>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        images={previewImages}
      />
    </>
  );
};

export default TemplateButtons;
